import React from 'react';
import './PrivacyPolicy.css';

const PrivacyPolicy = () => {
  return (
    <div className="privacy-policy-container">
      <h1 className="privacy-title">PRIVACY POLICY</h1>
      <p className="date">Last Updated: March 6, 2025</p>
      <p className="intro">
        Welcome to ClientBloom.ai. The services on our site are provided by Client Bloom LLC, a limited liability company formed in Wyoming. This Privacy Policy explains how we collect, use, and protect your information when you use our Customer Success Management platform. By using our services, you agree to this policy.
      </p>
      
      <section>
        <h2>1. INFORMATION WE COLLECT</h2>
        
        <h3>1.1. Account and Profile Information</h3>
        <p>
          When you create an account or use our platform, we collect basic information such as your name, email address, phone number, company name, and password. This information is necessary to provide you with access to our services and to communicate with you about your account.
        </p>

        <h3>1.2. Client Data You Input</h3>
        <p>
          As part of our Customer Success Management platform, you may input information about your clients, including names, email addresses, phone numbers, company details, billing status, engagement metrics, and notes. This is the core data you manage within our platform to track and improve customer relationships.
        </p>

        <h3>1.3. Payment Information</h3>
        <p>
          We collect payment information such as credit card details and billing addresses to process your subscription fees. All payment processing is handled securely through Stripe, and we do not store your complete credit card information on our servers.
        </p>

        <h3>1.4. Usage and Technical Data</h3>
        <p>
          We automatically collect information about how you use our platform, including pages visited, features used, and interaction patterns. We also collect device information such as browser type, operating system, IP address, and access times to maintain security and improve our services.
        </p>

        <h3>1.5. Zoom Integration Data</h3>
        <p>
          When you connect your Zoom account to ClientBloom.ai, we access and collect specific data from your Zoom account to provide enhanced customer success insights. This includes:
        </p>
        <ul>
          <li>Zoom account information (user ID, email, display name)</li>
          <li>Meeting recordings and cloud transcriptions</li>
          <li>Meeting metadata (date, time, duration, participant information)</li>
          <li>OAuth tokens to maintain the integration</li>
        </ul>
        <p>
          See Section 5 below for detailed information about how we use Zoom data.
        </p>
      </section>

      <section>
        <h2>2. HOW WE USE YOUR INFORMATION</h2>
        
        <h3>2.1. To Provide and Improve Our Services</h3>
        <p>
          We use your information to operate our Customer Success Management platform, including tracking client engagement, calculating retention scores, generating reports, and providing insights to help you improve customer relationships.
        </p>

        <h3>2.2. To Process Transactions</h3>
        <p>
          We use your payment information to process subscription fees and manage your billing. This includes sending invoices, processing refunds, and maintaining payment records.
        </p>

        <h3>2.3. To Communicate With You</h3>
        <p>
          We use your contact information to send account-related notifications, respond to support requests, provide updates about our services, and communicate important changes to our Terms or Privacy Policy.
        </p>

        <h3>2.4. To Maintain Security and Prevent Fraud</h3>
        <p>
          We monitor usage patterns and technical data to detect and prevent fraudulent activity, unauthorized access, and security threats to our platform and your data.
        </p>

        <h3>2.5. To Analyze and Improve</h3>
        <p>
          We use aggregated and anonymized usage data to understand how our platform is being used, identify areas for improvement, and develop new features.
        </p>
      </section>

      <section>
        <h2>3. HOW WE SHARE YOUR INFORMATION</h2>
        
        <h3>3.1. We Do Not Sell Your Data</h3>
        <p>
          We do not sell, rent, or trade your personal information or your clients' data to third parties for marketing purposes.
        </p>

        <h3>3.2. Service Providers</h3>
        <p>
          We share information with trusted third-party service providers who help us operate our platform:
        </p>
        <ul>
          <li><strong>Stripe:</strong> Payment processing and subscription management</li>
          <li><strong>AWS (Amazon Web Services):</strong> Cloud hosting, data storage, and infrastructure</li>
          <li><strong>Email Providers:</strong> Transactional emails and notifications</li>
          <li><strong>Analytics Tools:</strong> Platform usage analysis and performance monitoring</li>
        </ul>
        <p>
          These providers are contractually obligated to protect your data and use it only for the purposes we specify.
        </p>

        <h3>3.3. Legal Requirements</h3>
        <p>
          We may disclose your information if required by law, court order, or government regulation, or if we believe disclosure is necessary to protect our rights, your safety, or the safety of others.
        </p>

        <h3>3.4. Business Transfers</h3>
        <p>
          If we are involved in a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction. We will notify you before your information is transferred and becomes subject to a different privacy policy.
        </p>
      </section>

      <section>
        <h2>4. DATA RETENTION AND SECURITY</h2>
        
        <h3>4.1. How Long We Keep Your Data</h3>
        <p>
          We retain your account information and client data for as long as your account is active or as needed to provide services. If you close your account, we will delete or anonymize your data within 90 days, unless we are required by law to retain it longer.
        </p>

        <h3>4.2. Security Measures</h3>
        <p>
          We implement industry-standard security measures to protect your information, including:
        </p>
        <ul>
          <li>Encryption of data in transit (HTTPS/TLS) and at rest</li>
          <li>Secure authentication and access controls</li>
          <li>Regular security audits and monitoring</li>
          <li>Restricted access to personal data on a need-to-know basis</li>
        </ul>
        <p>
          While we take reasonable precautions to protect your data, no method of transmission over the internet or electronic storage is 100% secure. We cannot guarantee absolute security.
        </p>

        <h3>4.3. Your Responsibilities</h3>
        <p>
          You are responsible for maintaining the confidentiality of your account credentials and for any activity that occurs under your account. Notify us immediately if you suspect unauthorized access.
        </p>
      </section>

      <section>
        <h2>5. ZOOM INTEGRATION PRIVACY PRACTICES</h2>
        
        <h3>5.1. What the Zoom Integration Does</h3>
        <p>
          When you connect your Zoom account to ClientBloom.ai, our integration automatically syncs your Zoom cloud recordings and analyzes them to help you improve customer success. Specifically, the integration:
        </p>
        <ul>
          <li><strong>Authentication:</strong> Uses OAuth 2.0 to securely connect your Zoom account</li>
          <li><strong>Daily Sync:</strong> Automatically checks for new cloud recordings each day and downloads transcriptions in VTT format</li>
          <li><strong>Client Matching:</strong> Identifies which ClientBloom clients participated in each meeting</li>
          <li><strong>AI Analysis:</strong> Analyzes meeting transcripts to detect sentiment, conversation topics, and potential customer issues</li>
          <li><strong>Score Updates:</strong> Updates Client Retention Scores (CRS) based on call quality and client engagement</li>
          <li><strong>Recording Management:</strong> Provides access to recordings and transcripts within ClientBloom</li>
          <li><strong>Monitoring:</strong> Tracks call frequency and engagement patterns over time</li>
        </ul>

        <h3>5.2. What Zoom Data We Access</h3>
        <p><strong>✅ We DO access:</strong></p>
        <ul>
          <li>Cloud recordings and their transcriptions (VTT format)</li>
          <li>Meeting metadata (date, time, duration, topic)</li>
          <li>Participant information (names, emails of attendees)</li>
          <li>Your Zoom account details (user ID, email, display name)</li>
        </ul>
        
        <p><strong>❌ We DO NOT access:</strong></p>
        <ul>
          <li>Zoom meetings where cloud recording is disabled</li>
          <li>Local recordings stored on your computer</li>
          <li>Chat messages from Zoom meetings</li>
          <li>Personal Zoom settings or configurations</li>
          <li>Zoom contacts or address book</li>
          <li>Zoom meetings from other users in your organization (only your own)</li>
        </ul>

        <h3>5.3. How We Use Zoom Data</h3>
        <p>
          We use the data accessed from your Zoom account for the following specific purposes:
        </p>
        <ul>
          <li>Analyzing call quality, sentiment, and engagement to calculate retention risk</li>
          <li>Identifying topics discussed and action items mentioned during calls</li>
          <li>Tracking communication frequency with each client</li>
          <li>Updating Client Retention Scores based on interaction data</li>
          <li>Providing you with AI-generated insights and recommendations</li>
          <li>Displaying call history and transcripts within your ClientBloom dashboard</li>
        </ul>
        <p>
          <strong>Important Limitations:</strong>
        </p>
        <ul>
          <li>We do not use your Zoom data to train AI models on other customers' data</li>
          <li>We do not share your Zoom recordings with third parties (except as required for processing, e.g., AWS hosting)</li>
          <li>We do not use Zoom data for advertising or marketing purposes</li>
          <li>Your Zoom data is isolated to your ClientBloom workspace and is not visible to other ClientBloom users</li>
        </ul>

        <h3>5.4. Data Processing and Storage</h3>
        <p>
          Zoom recordings and transcriptions are processed and stored securely:
        </p>
        <ul>
          <li><strong>Location:</strong> Stored on AWS servers in the United States</li>
          <li><strong>Encryption:</strong> All data is encrypted in transit (TLS/HTTPS) and at rest</li>
          <li><strong>Access:</strong> Only accessible by you and authorized members of your ClientBloom workspace</li>
          <li><strong>Duration:</strong> Zoom data is retained as long as your account is active, or until you disconnect the integration</li>
        </ul>

        <h3>5.5. Meeting Participant Privacy and Consent</h3>
        <p>
          <strong>Your Responsibility:</strong> When you enable the Zoom integration, you are responsible for:
        </p>
        <ul>
          <li>Informing meeting participants that the call is being recorded</li>
          <li>Obtaining necessary consent for recording and processing (as required by law in your jurisdiction)</li>
          <li>Complying with Zoom's own Terms of Service and Recording Consent requirements</li>
          <li>Ensuring recordings only include meetings where participants have been properly notified</li>
        </ul>
        <p>
          We rely on you to manage consent appropriately. ClientBloom is not responsible for recordings made without proper consent.
        </p>

        <h3>5.6. Disconnecting the Zoom Integration</h3>
        <p>
          You can disconnect the Zoom integration at any time from the Admin Panel → Integrations section. When you disconnect:
        </p>
        <p><strong>Immediate Effects:</strong></p>
        <ul>
          <li>Your Zoom OAuth tokens are permanently deleted from our database</li>
          <li>Your tokens are revoked with Zoom's authorization server</li>
          <li>The integration is removed from your account</li>
          <li>ClientBloom will no longer access or sync any data from your Zoom account</li>
        </ul>
        <p><strong>Historical Data:</strong></p>
        <ul>
          <li>Previously synced recordings, transcripts, and call scores remain in your ClientBloom account</li>
          <li>This historical data is preserved because it's part of your customer success records</li>
          <li>If you want to delete historical Zoom data, you can do so by deleting individual calls or closing your account entirely</li>
        </ul>
        <p><strong>Reconnection:</strong></p>
        <ul>
          <li>You can reconnect your Zoom account at any time by going through the OAuth flow again</li>
          <li>After reconnecting, new recordings will be synced but historical recordings from the disconnected period will not be retroactively imported</li>
        </ul>

        <h3>5.7. Zoom's Privacy Policy</h3>
        <p>
          In addition to this Privacy Policy, your use of Zoom is also governed by Zoom's own Privacy Statement and Terms of Service:
        </p>
        <ul>
          <li><a href="https://explore.zoom.us/en/privacy/" target="_blank" rel="noopener noreferrer">Zoom Privacy Statement</a></li>
          <li><a href="https://explore.zoom.us/en/terms/" target="_blank" rel="noopener noreferrer">Zoom Terms of Service</a></li>
        </ul>
        <p>
          Please review Zoom's policies to understand how they collect and use data from your Zoom account.
        </p>

        <h3>5.8. Your Rights Regarding Zoom Data</h3>
        <p>
          You have the same rights over Zoom data stored in ClientBloom as you do over other data:
        </p>
        <ul>
          <li><strong>Access:</strong> You can view all Zoom recordings and transcripts synced to your account</li>
          <li><strong>Deletion:</strong> You can delete individual call recordings or disconnect the integration entirely</li>
          <li><strong>Export:</strong> You can export your data, including Zoom-related information, by contacting support</li>
          <li><strong>Correction:</strong> You can edit client associations and notes related to Zoom calls</li>
        </ul>
        <p>
          If you have concerns about how we process Zoom data, please contact us at hello@clientbloom.ai.
        </p>
      </section>

      <section>
        <h2>6. YOUR PRIVACY RIGHTS</h2>
        
        <h3>6.1. Access and Portability</h3>
        <p>
          You have the right to access your personal information and request a copy of the data we hold about you. Contact us at hello@clientbloom.ai to request your data.
        </p>

        <h3>6.2. Correction and Updates</h3>
        <p>
          You can update your account information and profile settings directly through the platform. If you need assistance correcting your data, contact our support team.
        </p>

        <h3>6.3. Deletion</h3>
        <p>
          You can request deletion of your account and associated data by contacting us at hello@clientbloom.ai. We will delete your data within 90 days unless we are required by law to retain certain information.
        </p>

        <h3>6.4. Opt-Out of Communications</h3>
        <p>
          You can opt out of promotional emails by clicking the unsubscribe link in any marketing email. However, you cannot opt out of essential account-related communications such as billing notifications or security alerts.
        </p>

        <h3>6.5. Do Not Track</h3>
        <p>
          Our platform does not currently respond to "Do Not Track" signals from web browsers. You can manage cookies through your browser settings.
        </p>
      </section>

      <section>
        <h2>7. COOKIES AND TRACKING TECHNOLOGIES</h2>
        <p>
          We use cookies and similar tracking technologies to improve your experience and analyze platform usage:
        </p>
        <ul>
          <li><strong>Essential Cookies:</strong> Required for authentication and platform functionality</li>
          <li><strong>Analytics Cookies:</strong> Help us understand how users interact with our platform</li>
          <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
        </ul>
        <p>
          You can control cookies through your browser settings, but disabling essential cookies may affect platform functionality.
        </p>
      </section>

      <section>
        <h2>8. CHILDREN'S PRIVACY</h2>
        <p>
          Our services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If you believe we have inadvertently collected information from a child, please contact us immediately.
        </p>
      </section>

      <section>
        <h2>9. INTERNATIONAL DATA TRANSFERS</h2>
        <p>
          Your information is stored and processed on servers located in the United States. If you are accessing our services from outside the United States, please be aware that your information may be transferred to, stored, and processed in the United States where our servers and service providers are located.
        </p>
      </section>

      <section>
        <h2>10. CHANGES TO THIS PRIVACY POLICY</h2>
        <p>
          We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. We will notify you of significant changes by email or through a prominent notice on our platform. Your continued use of our services after changes are made constitutes acceptance of the updated policy.
        </p>
      </section>

      <section>
        <h2>11. CONTACT US</h2>
        <p>
          If you have questions or concerns about this Privacy Policy or our data practices, please contact us at:
        </p>
        <address>
          Client Bloom LLC<br />
          30 N Gould St Ste R<br />
          Sheridan, WY 82801<br />
          Email: hello@clientbloom.ai
        </address>
      </section>

      <p className="final-note">
        By using ClientBloom.ai, you acknowledge that you have read, understood, and agreed to this Privacy Policy.
      </p>
    </div>
  );
};

export default PrivacyPolicy;
